/**
 * Feed API — Supabase RPC + CRUD for posts, likes, comments, search.
 * All functions use .throwOnError() per project rules.
 * All external data validated with Zod schemas.
 */

import { supabase } from '@turnifly/shared/api'
import { z } from 'zod/v4'
import {
  FeedItemSchema,
  CommentWithAuthorSchema,
  QuickCommentSchema,
} from '@turnifly/shared/types'
import type {
  FeedItem,
  CommentWithAuthor,
  QuickComment,
} from '@turnifly/shared/types'

// Re-export types for convenience
export type { FeedItem, CommentWithAuthor, QuickComment }

// ── USER SEARCH ───────────────────────────────────────────

const SearchUserSchema = z.object({
  id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable().catch(null),
  badge_type: z.string().nullable().catch(null),
  username: z.string().nullable().catch(null),
  bio: z.string().nullable().catch(null),
  is_premium: z.boolean().catch(false),
  is_private: z.boolean().catch(false),
  is_following: z.boolean().catch(false),
})

export type SearchUser = z.infer<typeof SearchUserSchema>

/**
 * User search — uses search_users RPC for proper bidirectional block
 * filtering and to avoid leaking deleted/incomplete profiles. Private
 * profiles are still returned (so they can be discovered and followed)
 * but their content is gated via posts_select RLS.
 */
export async function searchUsers(
  query: string,
  currentUserId: string,
  limit: number = 20,
): Promise<SearchUser[]> {
  const trimmed = query.trim()
  if (trimmed.length < 2) return []

  const { data } = await supabase
    .rpc('search_users', {
      p_user_id: currentUserId,
      p_query: trimmed,
      p_limit: limit,
      p_offset: 0,
    })
    .throwOnError()

  return z.array(SearchUserSchema).parse(data ?? [])
}

// ── FOLLOW STATUS (batch for feed) ────────────────────────

export interface FollowSets {
  accepted: Set<string>
  pending: Set<string>
}

export async function fetchFollowingSets(userId: string): Promise<FollowSets> {
  const { data } = await supabase
    .from('follows')
    .select('following_id, status')
    .eq('follower_id', userId)
    .throwOnError()

  const accepted = new Set<string>()
  const pending = new Set<string>()
  for (const r of (data ?? []) as Array<{ following_id: string; status: string }>) {
    if (r.status === 'accepted') accepted.add(r.following_id)
    else if (r.status === 'pending') pending.add(r.following_id)
  }
  return { accepted, pending }
}

// ── FEED QUERIES ──────────────────────────────────────────

export async function fetchForYouFeed(
  userId: string,
  cursor: number | null,
  limit: number = 20,
): Promise<FeedItem[]> {
  const { data } = await supabase
    .rpc('get_for_you_feed', {
      p_user_id: userId,
      p_cursor: cursor,
      p_limit: limit,
    })
    .throwOnError()

  return z.array(FeedItemSchema).parse(data ?? [])
}

export async function fetchFollowingFeed(
  userId: string,
  cursor: string | null,
  limit: number = 20,
): Promise<FeedItem[]> {
  const { data } = await supabase
    .rpc('get_following_feed', {
      p_user_id: userId,
      p_cursor: cursor,
      p_limit: limit,
    })
    .throwOnError()

  return z.array(FeedItemSchema).parse(data ?? [])
}

// ── VIEW TRACKING ─────────────────────────────────────────

export async function recordPostView(postId: string, userId: string): Promise<void> {
  await supabase
    .rpc('record_post_view', { p_post_id: postId, p_user_id: userId })
    .then(null, () => {}) // non-critical, fire-and-forget
}

// ── LIKE ──────────────────────────────────────────────────

export async function likePost(postId: string, userId: string): Promise<void> {
  await supabase
    .from('likes')
    .insert({ post_id: postId, user_id: userId })
    .throwOnError()
}

export async function unlikePost(postId: string, userId: string): Promise<void> {
  await supabase
    .from('likes')
    .delete()
    .eq('post_id', postId)
    .eq('user_id', userId)
    .throwOnError()
}

// ── CREATE POST ───────────────────────────────────────────

export interface CreatePostParams {
  userId: string
  content?: string
  mediaUrls?: string[]
  mediaType?: 'image' | 'video' | null
}

export async function createPost(params: CreatePostParams): Promise<unknown> {
  const { data } = await supabase
    .from('posts')
    .insert({
      user_id: params.userId,
      type: 'user_post',
      content: params.content ?? null,
      media_urls: params.mediaUrls ?? [],
      media_type: params.mediaType ?? null,
    })
    .select()
    .single()
    .throwOnError()
  return data
}

export async function deletePost(postId: string): Promise<void> {
  await supabase
    .rpc('soft_delete_post', { p_post_id: postId })
    .throwOnError()
}

// ── COMMENTS ──────────────────────────────────────────────

export async function fetchComments(
  postId: string,
  userId: string,
): Promise<CommentWithAuthor[]> {
  // 1. Fetch comments (no join — comments FK goes to auth.users, not profiles)
  const { data } = await supabase
    .from('comments')
    .select('id, post_id, user_id, parent_id, text, likes_count, is_quick, created_at')
    .eq('post_id', postId)
    .eq('is_hidden', false)
    .order('created_at', { ascending: true })
    .throwOnError()

  if (!data || data.length === 0) return []

  const comments = data as Array<Record<string, unknown>>

  // 2. Batch fetch profiles for all comment authors
  const authorIds = [...new Set(comments.map((c) => c.user_id as string))]
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, badge_type')
    .in('id', authorIds)
    .throwOnError()

  const profileMap = new Map<string, Record<string, unknown>>()
  for (const p of (profiles ?? []) as Array<Record<string, unknown>>) {
    profileMap.set(p.id as string, p)
  }

  // 3. Batch fetch user's likes on these comments
  const commentIds = comments.map((c) => c.id as string)
  const { data: myLikes } = await supabase
    .from('comment_likes')
    .select('comment_id')
    .eq('user_id', userId)
    .in('comment_id', commentIds)
    .throwOnError()

  const likedSet = new Set((myLikes ?? []).map((l: { comment_id: string }) => l.comment_id))

  // 4. Combine
  const rows = comments.map((row) => {
    const profile = profileMap.get(row.user_id as string)
    return {
      id: row.id,
      post_id: row.post_id,
      user_id: row.user_id,
      parent_id: row.parent_id ?? null,
      text: row.text,
      likes_count: row.likes_count,
      is_quick: row.is_quick,
      created_at: row.created_at,
      author_name: (profile?.full_name as string) ?? '',
      author_avatar: (profile?.avatar_url as string) ?? null,
      author_badge: (profile?.badge_type as string) ?? null,
      is_liked_by_me: likedSet.has(row.id as string),
    }
  })

  return z.array(CommentWithAuthorSchema).parse(rows)
}

export async function createComment(
  postId: string,
  userId: string,
  text: string,
  parentId?: string | null,
  isQuick?: boolean,
): Promise<unknown> {
  const { data } = await supabase
    .from('comments')
    .insert({
      post_id: postId,
      user_id: userId,
      parent_id: parentId ?? null,
      text,
      is_quick: isQuick ?? false,
    })
    .select()
    .single()
    .throwOnError()
  return data
}

export async function likeComment(commentId: string, userId: string): Promise<void> {
  await supabase
    .from('comment_likes')
    .insert({ comment_id: commentId, user_id: userId })
    .throwOnError()
}

export async function unlikeComment(commentId: string, userId: string): Promise<void> {
  await supabase
    .from('comment_likes')
    .delete()
    .eq('comment_id', commentId)
    .eq('user_id', userId)
    .throwOnError()
}

// ── QUICK COMMENTS ────────────────────────────────────────

export async function fetchQuickComments(): Promise<QuickComment[]> {
  const { data } = await supabase
    .from('quick_comments')
    .select('id, text_uz, text_en, context, icon, sort_order')
    .eq('is_active', true)
    .order('sort_order')
    .throwOnError()

  return z.array(QuickCommentSchema).parse(data ?? [])
}

// ── USER POSTS (for profile lenta tab) ────────────────────

/**
 * Profile lenta — privacy-enforced server-side via get_user_posts_safe RPC.
 * Returns an empty array when the viewer cannot see the target's content
 * (private profile + not following, or blocked). The RPC also filters out
 * reposts whose originals are not visible to the viewer.
 *
 * @param _currentUserId kept for hook stability; viewer is read from auth.uid()
 *   inside the RPC. Frontend never trusts a client-supplied viewer id.
 */
export async function fetchUserPosts(
  targetUserId: string,
  _currentUserId: string,
  cursor: string | null,
  limit: number = 20,
): Promise<FeedItem[]> {
  const { data } = await supabase
    .rpc('get_user_posts_safe', {
      p_target_user_id: targetUserId,
      p_cursor: cursor,
      p_limit: limit,
    })
    .throwOnError()

  return z.array(FeedItemSchema).parse(data ?? [])
}

// ── REPOST ────────────────────────────────────────────────

/**
 * Repost a public post. Privacy enforced server-side via
 * create_repost_safe RPC: the caller must currently be able to
 * see the original (own post is rejected, private content from
 * non-followed users is rejected).
 *
 * @param _userId kept for hook stability; the RPC reads auth.uid()
 *   so the caller cannot impersonate another user.
 */
export async function createRepost(
  _userId: string,
  originalPostId: string,
  caption?: string,
): Promise<unknown> {
  const { data } = await supabase
    .rpc('create_repost_safe', {
      p_original_post_id: originalPostId,
      p_caption: caption ?? null,
    })
    .throwOnError()

  return data
}

// ── REPORT ────────────────────────────────────────────────

export async function reportPost(
  postId: string,
  userId: string,
  reason: string,
  description?: string,
): Promise<void> {
  await supabase
    .from('reports')
    .insert({
      reporter_id: userId,
      target_type: 'post',
      target_id: postId,
      reason,
      description: description ?? null,
    })
    .throwOnError()
}
